import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Dialog, DialogContent, IconButton, Link, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import eNetsDDImg from '../../img/eNETS_DD.png';
import eNetsQRImg from '../../img/eNETS_QR.png';
import { fetchPaymentConfig, initiatePayment } from '../services/api';

type PayView = 'method-select' | 'initiating' | 'payment-active';

/**
 * Rendered once and never updated by React. NETS JS injects its payment UI
 * (or error page) into the #ajaxResponse div via innerHTML. If React were
 * allowed to re-render it, it would wipe NETS-injected DOM children.
 * React.memo with no props guarantees zero re-renders.
 */
const NetsPaymentContainer = React.memo(() => (
  <div
    id="ajaxResponse"
    style={{
      minHeight: 300,
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      borderRadius: 8,
      color: '#111111',
      backgroundColor: '#ffffff',
      fontFamily: 'Arial, sans-serif',
    }}
  />
));

function loadScriptOnce(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function resolveNetsAssetUrls(container: HTMLElement, gatewayBase: string): void {
  container.querySelectorAll<HTMLImageElement>('img[src], input[type="image"][src]').forEach((image) => {
    const rawSrc = image.getAttribute('src')?.trim();
    if (!rawSrc || /^(?:data:|blob:|https?:|\/\/)/i.test(rawSrc)) return;
    try {
      image.src = new URL(rawSrc, `${gatewayBase.replace(/\/$/, '')}/`).href;
    } catch {
      // Leave malformed third-party asset URLs untouched.
    }
  });

  container.querySelectorAll<HTMLLinkElement>('link[href]').forEach((link) => {
    const rawHref = link.getAttribute('href')?.trim();
    if (!rawHref || /^(?:data:|https?:|\/\/)/i.test(rawHref)) return;
    try {
      link.href = new URL(rawHref, `${gatewayBase.replace(/\/$/, '')}/`).href;
    } catch {
      // Leave malformed third-party asset URLs untouched.
    }
  });
}

export interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-known amount (S$). If omitted, the dialog fetches /payment/config. */
  amountSgd?: number | null;
  /** Optional heading shown above the method selector. */
  title?: string;
  /** Optional supporting line under the title. */
  subtitle?: string;
  /** Admin payment-request token, so a settled payment reconciles to its request. */
  requestToken?: string | null;
}

/**
 * Reusable eNETS payment flow. On a successful payment, eNETS browser-redirects
 * the whole window to /super-connector?action=paymentprocessing&ref=… (via the
 * backend B2S handler), so this dialog does not need an onSuccess callback.
 * The app polls the backend and shows success only after the request is PAID.
 * A failed payment redirects back to /super-connector?payment_failed=1&reason=…
 */
export default function PaymentDialog({ open, onClose, amountSgd: amountProp, title, subtitle, requestToken }: PaymentDialogProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const methodBorder = isDark ? '#64748b' : '#cbd5e1';
  const methodBackground = isDark ? '#172033' : '#ffffff';
  const selectedMethodBackground = alpha('#ed8936', isDark ? 0.12 : 0.08);
  const [payView, setPayView] = useState<PayView>('method-select');
  const [selectedMethod, setSelectedMethod] = useState<'DD' | 'QR'>('DD');
  const [initError, setInitError] = useState('');
  const [amountSgd, setAmountSgd] = useState<number | null>(amountProp ?? null);
  const txnDataRef = useRef<{ txnReq: string; mac: string; keyId: string; gatewayUrl: string } | null>(null);

  // Resolve the amount to display.
  useEffect(() => {
    if (!open) return;
    if (amountProp != null) { setAmountSgd(amountProp); return; }
    fetchPaymentConfig()
      .then((cfg) => setAmountSgd(cfg.amountSgd))
      .catch(() => setAmountSgd(50)); // fallback if fetch fails
  }, [open, amountProp]);

  // Reset when the dialog closes so a re-open starts clean.
  useEffect(() => {
    if (!open) {
      setPayView('method-select');
      setInitError('');
      setSelectedMethod('DD');
      txnDataRef.current = null;
    }
  }, [open]);

  // Keep the third-party eNETS markup contained and readable at every breakpoint.
  // eNETS injects relative asset paths, so resolve them against the gateway host.
  useEffect(() => {
    const styleId = 'nets-layout-fix';
    if (open && payView === 'payment-active') {
      const gatewayUrl = txnDataRef.current?.gatewayUrl;
      const gatewayBase = gatewayUrl?.replace(/\/api\/.*$/, '');
      const container = document.getElementById('ajaxResponse');

      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          #ajaxResponse,
          #ajaxResponse * {
            box-sizing: border-box !important;
          }
          #ajaxResponse {
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
          }
          #ajaxResponse .container,
          #ajaxResponse .container-fluid {
            width: 100% !important;
            max-width: 100% !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          #ajaxResponse .row {
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          #ajaxResponse .container-fluid > .row {
            display: flex !important;
            flex-wrap: wrap;
            justify-content: center !important;
          }
          #ajaxResponse .container-fluid > .row > div[class*="col-lg-8"],
          #ajaxResponse .container-fluid > .row > div[class*="col-md-8"] {
            display: none !important;
          }
          #ajaxResponse .container-fluid > .row > div[class*="col-"] {
            float: none !important;
            width: 100% !important;
            max-width: 100% !important;
            flex: 0 0 100% !important;
          }
          #ajaxResponse img,
          #ajaxResponse input[type="image"] {
            max-width: 100% !important;
            height: auto;
          }
          #ajaxResponse form,
          #ajaxResponse table {
            max-width: 100% !important;
          }
          @media (max-width: 600px) {
            #ajaxResponse .row {
              display: flex !important;
              flex-wrap: wrap !important;
            }
            #ajaxResponse .row > div[class*="col-"] {
              float: none !important;
              width: 100% !important;
              max-width: 100% !important;
              flex: 0 0 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            #ajaxResponse button,
            #ajaxResponse input[type="button"],
            #ajaxResponse input[type="submit"] {
              min-height: 42px;
            }
          }
        `;
        document.head.appendChild(style);
      }

      let observer: MutationObserver | null = null;
      if (container && gatewayBase) {
        const repairAssets = () => resolveNetsAssetUrls(container, gatewayBase);
        observer = new MutationObserver(repairAssets);
        observer.observe(container, { childList: true, subtree: true });
        repairAssets();
      }

      return () => {
        observer?.disconnect();
        document.getElementById(styleId)?.remove();
      };
    } else {
      document.getElementById(styleId)?.remove();
    }
    return () => { document.getElementById(styleId)?.remove(); };
  }, [open, payView]);

  // Load NETS JS and invoke sendPayLoad once #ajaxResponse is in the DOM.
  useEffect(() => {
    if (!open || payView !== 'payment-active' || !txnDataRef.current) return;
    const { txnReq, mac, keyId, gatewayUrl } = txnDataRef.current;
    const scriptBase = gatewayUrl.replace(/\/api\/.*$/, '');
    loadScriptOnce(`${scriptBase}/js/jquery-3.1.1.min.js`)
      .then(() => loadScriptOnce(`${scriptBase}/pluginpages/env.jsp`))
      .then(() => loadScriptOnce(`${scriptBase}/js/apps.js`))
      .then(() => {
        (window as unknown as { sendPayLoad: (t: string, m: string, k: string) => void })
          .sendPayLoad(txnReq, mac, keyId);
      })
      .catch((err) => {
        console.error('NETS script load failed', err);
        setInitError('Failed to load payment gateway. Please try again.');
        setPayView('method-select');
      });
  }, [open, payView]);

  const handleNext = async () => {
    setInitError('');
    setPayView('initiating');
    try {
      const result = await initiatePayment(selectedMethod, requestToken || undefined);
      txnDataRef.current = result;
      setPayView('payment-active');
    } catch (e) {
      setInitError(e instanceof Error ? e.message : 'Failed to initiate payment');
      setPayView('method-select');
    }
  };

  const isPaymentActive = payView === 'payment-active';
  const amountLabel = amountSgd !== null ? `S$${amountSgd}` : '…';

  return (
    <Dialog
      open={open}
      // Don't let a backdrop click interrupt the live NETS widget.
      onClose={isPaymentActive ? undefined : onClose}
      maxWidth={isPaymentActive ? 'md' : 'xs'}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: { xs: 2, sm: 2.5 },
          m: { xs: 2, sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2.5, sm: 3 }, pt: 2.5, gap: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1.3 }}>
          {title || 'Super Connector setup payment'}
        </Typography>
        {!isPaymentActive && (
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 2, pb: { xs: 2.5, sm: 3 } }}>
        {isPaymentActive ? (
          <>
            <NetsPaymentContainer />
            <Box sx={{ py: 1.5, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider', mt: 1 }}>
              <Link component="button" variant="caption" onClick={onClose} sx={{ color: 'text.secondary', cursor: 'pointer' }}>
                Cancel and go back
              </Link>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.55 }}>
              {subtitle || `Your one-time setup fee of ${amountLabel} is due. Choose an eNETS payment method to continue.`}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: { xs: 1.5, sm: 2 }, mb: 3 }}>
              {/* eNETS Debit */}
              <ButtonBase
                onClick={() => setSelectedMethod('DD')}
                aria-pressed={selectedMethod === 'DD'}
                sx={{
                  width: '100%',
                  border: `2px solid ${selectedMethod === 'DD' ? '#ed8936' : methodBorder}`,
                  bgcolor: selectedMethod === 'DD' ? selectedMethodBackground : methodBackground,
                  borderRadius: 2, p: { xs: 1.5, sm: 3 }, minHeight: { xs: 142, sm: 156 },
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  position: 'relative', transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': { borderColor: '#ed8936', bgcolor: selectedMethodBackground },
                }}
              >
                {selectedMethod === 'DD' && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, bgcolor: '#ed8936', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: '#fff', fontSize: 13, lineHeight: 1, fontWeight: 700 }}>✓</Typography>
                  </Box>
                )}
                <Box component="img" src={eNetsDDImg} alt="eNETS Debit" sx={{ width: '100%', maxWidth: 120, height: { xs: 42, sm: 52 }, objectFit: 'contain', display: 'block', mb: 1.25 }} />
                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>eNETS Debit</Typography>
              </ButtonBase>

              {/* eNETS QR */}
              <ButtonBase
                onClick={() => setSelectedMethod('QR')}
                aria-pressed={selectedMethod === 'QR'}
                sx={{
                  width: '100%',
                  border: `2px solid ${selectedMethod === 'QR' ? '#ed8936' : methodBorder}`,
                  bgcolor: selectedMethod === 'QR' ? selectedMethodBackground : methodBackground,
                  borderRadius: 2, p: { xs: 1.5, sm: 3 }, minHeight: { xs: 142, sm: 156 },
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  position: 'relative', transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': { borderColor: '#ed8936', bgcolor: selectedMethodBackground },
                }}
              >
                {selectedMethod === 'QR' && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, bgcolor: '#ed8936', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: '#fff', fontSize: 13, lineHeight: 1, fontWeight: 700 }}>✓</Typography>
                  </Box>
                )}
                <Box component="img" src={eNetsQRImg} alt="eNETS QR" sx={{ width: '100%', maxWidth: 72, height: { xs: 48, sm: 52 }, objectFit: 'contain', display: 'block', mb: 1.25 }} />
                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>eNETS QR</Typography>
              </ButtonBase>
            </Box>

            {initError && <Alert severity="error" sx={{ mb: 2 }}>{initError}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, '& > button': { flex: { xs: 1, sm: '0 0 auto' } } }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'divider', color: 'text.primary', '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={payView === 'initiating'}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#ed8936', color: '#111827', '&:hover': { bgcolor: '#f59e52' }, minWidth: { sm: 120 } }}
              >
                {payView === 'initiating' ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : `Pay ${amountLabel}`}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
